import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertCircle, CheckCircle, ChevronRight, Play, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

const RISK_COLORS = { LOW: "bg-emerald-600", MEDIUM: "bg-amber-500", HIGH: "bg-red-600" };

export default function FidelityAuditor() {
    const { id } = useParams(); // module_id
    const [module, setModule] = useState(null);
    const [report, setReport] = useState(null);
    const [scenes, setScenes] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [running, setRunning] = useState(false);
    const [signedByScene, setSignedByScene] = useState({});
    const [captionsByScene, setCaptionsByScene] = useState({});
    const [comment, setComment] = useState("");

    async function load() {
        const m = await api.get(`/modules/${id}`);
        const mod = m.data?.module || m.data;
        setModule(mod);
        const s = await api.get(`/modules/${id}/scenes`);
        setScenes(s.data || []);
        const cvid = mod?.current_version_id;
        if (cvid) {
            try {
                const a = await api.get(`/modules/${id}/versions/${cvid}/audit`);
                setReport(a.data);
            } catch (e) { /* no audit yet */ }
        }
    }
    useEffect(() => { load(); }, [id]);

    async function runAudit() {
        setRunning(true);
        try {
            const r = await api.post(`/modules/${id}/versions/${module.current_version_id}/audit`);
            setReport(r.data);
        } catch (e) { alert("Audit failed: " + (e.response?.data?.detail || e.message)); }
        finally { setRunning(false); }
    }

    const perScene = report?.per_scene || [];
    const sortedRisk = [...perScene].sort((a, b) => {
        const order = { HIGH: 0, MEDIUM: 1, LOW: 2 };
        return (order[a.risk] || 3) - (order[b.risk] || 3);
    });
    const selected = perScene.find((p) => p.scene_id === selectedId) || sortedRisk[0];
    const sceneRow = scenes.find((s) => s.id === selected?.scene_id);

    async function loadSelectedVideo() {
        if (!sceneRow) return;
        // Prefer selected_generation (Cinematic Engine) → motion → heygen
        if (sceneRow.selected_generation_id) {
            const g = await api.get(`/generations/${sceneRow.selected_generation_id}`);
            setSignedByScene({...signedByScene, [sceneRow.id]: g.data.signed_url});
        } else if (sceneRow.selected_motion_render_id) {
            const mr = await api.get(`/motion-renders/${sceneRow.selected_motion_render_id}`);
            setSignedByScene({...signedByScene, [sceneRow.id]: mr.data.signed_url});
        } else if (sceneRow.selected_heygen_generation_id) {
            const hg = await api.get(`/heygen/generations/${sceneRow.selected_heygen_generation_id}`);
            setSignedByScene({...signedByScene, [sceneRow.id]: hg.data.signed_url});
        }
    }
    useEffect(() => { if (sceneRow && !signedByScene[sceneRow.id]) loadSelectedVideo(); }, [sceneRow?.id]);

    async function act(findingIdx, action) {
        try {
            await api.post(`/scene-review-actions`, {
                scene_id: selected.scene_id, finding_index: findingIdx, action, comment: comment || null,
            });
            setComment("");
            alert(`Recorded ${action}`);
        } catch (e) { alert(e.response?.data?.detail || e.message); }
    }

    return (
        <div className="p-6">
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <div className="text-xs text-neutral-500 uppercase tracking-wider">Content check</div>
                    <h1 className="text-2xl font-semibold">{module?.name}</h1>
                    <p className="text-sm text-neutral-500 mt-1">AI-assisted content check — human review required.</p>
                </div>
                <Button onClick={runAudit} disabled={running || !module?.current_version_id} data-testid="btn-run-audit">
                    <ShieldCheck size={16} className="mr-2"/>{running ? "Running…" : "Run content check"}
                </Button>
            </div>
            {report && (
                <Card className="mb-6" data-testid="audit-summary-card">
                    <CardContent className="p-5 grid grid-cols-4 gap-6">
                        <div><div className="text-xs text-neutral-500 uppercase">Source alignment</div><div className="text-2xl font-semibold" data-testid="alignment-pct">{report.source_alignment_percent}%</div></div>
                        <div><div className="text-xs text-neutral-500 uppercase">Total findings</div><div className="text-2xl font-semibold">{report.total_findings}</div></div>
                        <div><div className="text-xs text-neutral-500 uppercase">High risk scenes</div><div className="text-2xl font-semibold text-red-500">{report.scenes_by_risk?.HIGH ?? 0}</div></div>
                        <div><div className="text-xs text-neutral-500 uppercase">Last audit</div><div className="text-sm mt-2">{report.last_audit_at?.slice(0,19).replace('T',' ')}</div></div>
                    </CardContent>
                </Card>
            )}
            {report ? (
                <div className="grid grid-cols-12 gap-4">
                    <aside className="col-span-3">
                        <div className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Scenes by risk</div>
                        {sortedRisk.map((p) => (
                            <button key={p.scene_id}
                                onClick={() => setSelectedId(p.scene_id)}
                                className={`w-full text-left px-3 py-2 rounded border mb-1.5 flex items-center gap-2 hover:border-neutral-500 ${selected?.scene_id === p.scene_id ? "border-white bg-neutral-900" : "border-neutral-800"}`}
                                data-testid={`fidelity-scene-${p.scene_number}`}
                            >
                                <span className={`w-2 h-2 rounded-full ${RISK_COLORS[p.risk] || 'bg-neutral-500'}`}/>
                                <span className="flex-1 text-sm">{p.scene_number}</span>
                                <span className="text-xs text-neutral-500">{p.findings.length}</span>
                            </button>
                        ))}
                    </aside>
                    <section className="col-span-5">
                        {sceneRow && signedByScene[sceneRow.id] ? (
                            <video controls src={signedByScene[sceneRow.id]} className="w-full aspect-video bg-black rounded" data-testid="fidelity-scene-video"/>
                        ) : (
                            <div className="w-full aspect-video bg-neutral-900 rounded flex items-center justify-center text-neutral-500"><Play/></div>
                        )}
                        {sceneRow && (
                            <div className="mt-3 text-sm space-y-2">
                                <div><span className="text-neutral-500">Voiceover: </span>{sceneRow.voiceover || "—"}</div>
                                <div><span className="text-neutral-500">Trainer: </span>{sceneRow.trainer_dialogue || "—"}</div>
                            </div>
                        )}
                    </section>
                    <section className="col-span-4">
                        <div className="text-xs text-neutral-500 uppercase tracking-wider mb-3">Findings ({selected?.findings?.length ?? 0})</div>
                        {selected?.findings?.length ? selected.findings.map((f, idx) => (
                            <Card key={idx} className="mb-3" data-testid={`finding-card-${idx}`}>
                                <CardContent className="p-4 text-sm">
                                    <div className="flex items-center justify-between mb-2">
                                        <Badge variant={f.severity === "HIGH" ? "destructive" : "secondary"}>{f.kind}</Badge>
                                        <span className="text-xs text-neutral-500">{f.severity}</span>
                                    </div>
                                    <div className="text-neutral-200 mb-2">{f.message}</div>
                                    {f.source_span && <div className="text-xs text-neutral-500">Source: <span className="text-neutral-300">"{f.source_span}"</span></div>}
                                    {f.generated_span && <div className="text-xs text-neutral-500">Generated: <span className="text-neutral-300">"{f.generated_span}"</span></div>}
                                    {f.suggested_fix && <div className="text-xs mt-2 text-emerald-400">Fix: {f.suggested_fix}</div>}
                                    <div className="flex gap-2 mt-3">
                                        <Button size="sm" variant="outline" onClick={() => act(idx, "ACCEPT")} data-testid={`btn-accept-${idx}`}><CheckCircle size={12} className="mr-1"/>Accept</Button>
                                        <Button size="sm" variant="outline" onClick={() => act(idx, "REJECT")} data-testid={`btn-reject-${idx}`}><AlertCircle size={12} className="mr-1"/>Reject</Button>
                                    </div>
                                </CardContent>
                            </Card>
                        )) : <div className="text-sm text-neutral-500 py-8 text-center">No findings on this scene.</div>}
                        <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Comment (optional, attaches to next Accept/Reject)" className="text-sm" data-testid="finding-comment-textarea"/>
                    </section>
                </div>
            ) : (
                <Card><CardContent className="py-16 text-center text-neutral-400" data-testid="no-audit-empty">Run the content check to see findings.</CardContent></Card>
            )}
        </div>
    );
}
